import {
  AgeGroup,
  AgeLimits,
  AMOUNT_PREMIUM_THRESHOLD,
  INCOME_PREMIUM_THRESHOLD
} from 'constants/components'

/**
 * Determines the age group for a debtor.
 *
 * The age bands are defined by `AgeLimits`: young includes ages up to and
 * including the young-age limit, middle includes the following ages up to and
 * including the middle-age limit, and old includes all higher ages.
 *
 * @param debtorsAge The debtor's age in years.
 * @returns The age group matching the debtor's age.
 */
export function getAgeGroupFromAge(debtorsAge: number) {
  if (debtorsAge <= AgeLimits.YOUNG_AGE_UNTIL) {
    return AgeGroup.YOUNG_AGE_GROUP
  }

  if (debtorsAge <= AgeLimits.MIDDLE_AGE_UNTIL) {
    return AgeGroup.MIDDLE_AGE_GROUP
  }

  return AgeGroup.OLD_AGE_GROUP
}

/**
 * Checks whether a debtor belongs to either the young or middle age group.
 *
 * This is the age classification used by premium-customer eligibility and
 * excludes the old age group.
 *
 * @param debtorsAge The debtor's age in years.
 * @returns `true` for young and middle age groups; otherwise `false`.
 */
export function isYoungOrMiddleAgeGroup(debtorsAge: number) {
  const ageGroup = getAgeGroupFromAge(debtorsAge)
  return (
    ageGroup === AgeGroup.YOUNG_AGE_GROUP ||
    ageGroup === AgeGroup.MIDDLE_AGE_GROUP
  )
}

export const CAT_4784_PREMIUM_AMOUNT_THRESHOLD = 40000

/**
 * Checks whether a debtor meets all premium-customer eligibility criteria.
 *
 * A customer is premium when they are in the young or middle age group and
 * meet or exceed both the configured loan amount and primary-income
 * thresholds. All criteria are inclusive at their threshold values.
 *
 * @param customer The debtor's age, requested amount, and primary income.
 * @returns `true` when every premium criterion is met; otherwise `false`.
 */
export function isPremiumCustomer({
  debtorsAge,
  amount,
  income,
  amountThreshold = AMOUNT_PREMIUM_THRESHOLD
}: {
  debtorsAge: number
  amount: number
  income: { primary: number }
  amountThreshold?: number
}) {
  return (
    isYoungOrMiddleAgeGroup(debtorsAge) &&
    amount >= amountThreshold &&
    income.primary >= INCOME_PREMIUM_THRESHOLD
  )
}
